/**
 * Header behaviors: mobile drawer (focus-trapped, page inerted),
 * desktop disclosure navigation with aria-expanded, live cart count.
 *
 * Nothing here caches a DOM node at module scope. The theme editor
 * re-renders the whole section on every edit, which detaches the old
 * nodes — a cached `drawer` reference would leave closeDrawer() operating
 * on a node that is no longer in the document. Element listeners are
 * bound through initHeader(), which is idempotent via data-nav-bound and
 * re-runs on shopify:section:load.
 *
 * This is the WAI-ARIA "Disclosure Navigation" pattern, NOT "Menubar":
 * Tab traverses, and there is deliberately no arrow-key handling or
 * roving tabindex. That is also why the summary carries no
 * aria-haspopup — that attribute promises menu semantics we do not
 * implement, and half a menubar is worse than none.
 */
import { subscribe, PUB_SUB_EVENTS, trapFocus, removeTrapFocus } from './global.js';

const DESKTOP = '(min-width: 990px)';
const pageRegions = ['.shopify-section-group-header-group', 'main', '.shopify-section-group-footer-group'];
let hideTimer = null;

const getDrawer = () => document.getElementById('MobileDrawer');
const getToggles = () => document.querySelectorAll('[data-drawer-toggle]');

function setPageInert(inert) {
  const drawer = getDrawer();
  pageRegions.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      if (drawer && el.contains(drawer)) return;
      el.toggleAttribute('inert', inert);
    });
  });
}

function openDrawer() {
  const drawer = getDrawer();
  if (!drawer) return;
  clearTimeout(hideTimer);
  drawer.hidden = false;
  requestAnimationFrame(() => drawer.classList.add('is-open'));
  document.body.style.overflow = 'hidden';
  getToggles().forEach((t) => t.setAttribute('aria-expanded', 'true'));
  setPageInert(true);
  const panel = drawer.querySelector('.header-drawer__panel');
  if (!panel) return;
  // Must be the close BUTTON, not the first [data-drawer-close] — the
  // overlay div carries that attribute too and sits earlier in the markup,
  // and a bare div cannot take focus, so focus would stay on the toggle.
  trapFocus(panel, panel.querySelector('button[data-drawer-close]') || panel);
}

function closeDrawer() {
  const drawer = getDrawer();
  if (!drawer) return;
  drawer.classList.remove('is-open');
  document.body.style.overflow = '';
  getToggles().forEach((t) => t.setAttribute('aria-expanded', 'false'));
  setPageInert(false);
  removeTrapFocus(getToggles()[0]);
  clearTimeout(hideTimer);
  hideTimer = setTimeout(() => {
    const d = getDrawer();
    if (d) d.hidden = true;
  }, 300);
}

/* A section re-render mid-open would strand `inert` on <main> and the
   scroll lock on <body>, with no drawer left to close them. */
function releaseDrawerState() {
  document.body.style.overflow = '';
  setPageInert(false);
  removeTrapFocus(null);
  clearTimeout(hideTimer);
}

function bindDrawer(root) {
  root.querySelectorAll('[data-drawer-toggle]').forEach((t) => {
    if (t.dataset.navBound) return;
    t.dataset.navBound = '1';
    t.addEventListener('click', openDrawer);
  });
  const drawer = getDrawer();
  if (!drawer) return;
  drawer.querySelectorAll('[data-drawer-close]').forEach((el) => {
    if (el.dataset.navBound) return;
    el.dataset.navBound = '1';
    el.addEventListener('click', closeDrawer);
  });
}

function bindDisclosures(root) {
  root.querySelectorAll('[data-hover-open]').forEach((details) => {
    if (details.dataset.navBound) return;
    details.dataset.navBound = '1';

    const summary = details.querySelector('summary');
    const item = details.closest('.header__menu-item');
    // Guard: an unguarded item.addEventListener throws during a re-render,
    // and the browser harness fails the whole run on any uncaught error.
    if (!summary || !item) return;

    let closeTimer;
    let openTimer;

    details.addEventListener('toggle', () => {
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    });
    summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');

    /* Hover intent. A cold hover waits ~100ms before opening, so sweeping
       the pointer across the header to reach the cart no longer strobes
       four full-bleed panels. But once ANY panel is open the user is in
       "menu mode": switching tabs closes the old panel and opens the new
       one instantly, cancelling its pending close timer so the handoff
       cannot double-fire. Timer hygiene is the whole trick here — every
       path clears both timers. */
    const OPEN_DELAY = 100;

    item.addEventListener('mouseenter', () => {
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      if (details.open) return;
      const other = document.querySelector('.header__disclosure[open]:not([data-keep-open])');
      if (other && other !== details) {
        other.removeAttribute('open');
        details.setAttribute('open', '');
        return;
      }
      openTimer = setTimeout(() => details.setAttribute('open', ''), OPEN_DELAY);
    });
    item.addEventListener('mouseleave', () => {
      clearTimeout(openTimer);
      if (details.hasAttribute('data-keep-open')) return;
      closeTimer = setTimeout(() => details.removeAttribute('open'), 150);
    });

    /* Clicking the tab of an ALREADY-OPEN panel navigates to its hub URL.
       Scope: mouse on hover-capable pointers only. Keyboard clicks
       (e.detail === 0) and touch keep native toggle semantics — for them
       the summary is a disclosure button, full stop. Without this, hover
       opened the panel and the natural follow-up click closed it, and it
       could not reopen until the pointer left and returned: the most
       instinctive interaction on the most important control did the
       wrong thing. The "View all X" row inside the panel remains the
       visible path to the same URL.

       Deliberately NOT opening panels on focusin: auto-open on focus
       would force keyboard users through every panel's 15-20 tab stops
       just to traverse the four tabs. Enter/Space opens (native
       disclosure), Escape closes; that is the WAI-ARIA disclosure
       pattern this header documents at the top of the file. */
    summary.addEventListener('click', (e) => {
      if (e.detail === 0) return;
      if (window.matchMedia('(hover: none)').matches) return;
      if (!details.open) return;
      const url = summary.dataset.url;
      if (!url) return;
      e.preventDefault();
      window.location.href = url;
    });
    details.addEventListener('focusout', (e) => {
      if (details.hasAttribute('data-keep-open')) return;
      // relatedTarget is null when focus moves to browser chrome or to a
      // non-focusable part of the panel. Closing a panel the pointer is
      // still inside would yank it out from under the cursor.
      if (e.relatedTarget === null && details.matches(':hover')) return;
      if (!details.contains(e.relatedTarget)) details.removeAttribute('open');
    });
  });
}

function initHeader(root = document) {
  bindDrawer(root);
  bindDisclosures(root);
}

initHeader();

/* ---------- global listeners: bound once, re-query on every event ---------- */

document.addEventListener('keyup', (e) => {
  const drawer = getDrawer();
  if (e.key === 'Escape' && drawer && !drawer.hidden) closeDrawer();
});

/* Escape for the desktop panels.
   global.js has its own Escape handler, but it is gated on
   event.target.closest('details[open]') — so it only fires when focus is
   already inside the panel. A panel opened by hover, with focus still on
   <body>, is not closable there. This runs on keydown, so by the time
   that keyup handler fires the panel is already closed and it no-ops. */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  document.querySelectorAll('.header__disclosure[open]:not([data-keep-open])').forEach((details) => {
    const hadFocus = details.contains(document.activeElement);
    details.removeAttribute('open');
    // Only pull focus back if it was inside; moving focus to a summary the
    // user never focused would be disorienting.
    if (hadFocus) details.querySelector('summary')?.focus();
  });
});

document.addEventListener('click', (e) => {
  document.querySelectorAll('.header__disclosure[open]').forEach((details) => {
    if (details.hasAttribute('data-keep-open')) return;
    if (!details.contains(e.target)) details.removeAttribute('open');
  });
});

/* Crossing back to desktop must not reveal a panel left open on mobile,
   where .header__nav is display:none and the state is invisible. */
const desktopQuery = window.matchMedia(DESKTOP);
desktopQuery.addEventListener('change', () => {
  document.querySelectorAll('.header__disclosure[open]').forEach((d) => d.removeAttribute('open'));
});

/* ---------- theme editor ---------- */

document.addEventListener('shopify:section:load', (e) => {
  if (!e.target.querySelector('.header-wrapper')) return;
  releaseDrawerState();
  initHeader(e.target);
});

document.addEventListener('shopify:section:unload', (e) => {
  if (e.target.querySelector('.header-wrapper')) releaseDrawerState();
});

/* Selecting a mega-menu promo block should open the panel it lives in and
   hold it open while the merchant edits. data-keep-open is the existing
   opt-out honoured by global.js's Escape handler. */
document.addEventListener('shopify:block:select', (e) => {
  const details = e.target.closest('.header__disclosure');
  if (!details) return;
  details.setAttribute('data-keep-open', '');
  details.setAttribute('open', '');
});

document.addEventListener('shopify:block:deselect', (e) => {
  const details = e.target.closest('.header__disclosure');
  if (!details) return;
  details.removeAttribute('data-keep-open');
  details.removeAttribute('open');
});

/* Cart count: visible bubble is decorative; the accessible count lives
   in a visually-hidden span inside the link. */
subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => {
  if (data?.cart?.item_count === undefined) return;
  const bubble = document.querySelector('[data-cart-count]');
  if (bubble) {
    bubble.textContent = Math.min(data.cart.item_count, 99);
    bubble.classList.toggle('hidden', data.cart.item_count === 0);
  }
  const srCount = document.querySelector('[data-cart-count-text]');
  if (srCount) {
    const template = window.shorelineStrings?.cartItemCount || '[count] items';
    srCount.textContent = template.replace('[count]', data.cart.item_count);
  }
});
